import React, { useState, useEffect, useMemo } from 'react';
import { FetchMoreQueryOptions, useQuery } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { withEndlessScroll } from '../../HOC/endlessScroll';
import { setError } from '../../../actions';
import SectionList from '../../SectionList';
import { SectionPlaceholder } from '../../Placeholders/SectionPlaceholder';
import { ItemCardPlaceholder } from '../../Placeholders/ItemCardPlaceholder';
import { useBookmarks } from '../../../hooks/useBookmarks';
import { ItemCard } from '../../ui/ItemCard';
import { vipItemsQuery } from '../../../api/queries';
import { PageWrapper } from '../../../styled/pageLayout.styles';
import { AdaptivePageHeader } from '../../ui/PageHeader/AdaptivePageHeader';
import { AllVippedLinkContainer, VipHeaderContainer } from './styled';
import { NewSectionHeader, RightLink } from '../../NewSectionHeader';
import useScreenSize from '../../../hooks/useScreenSize';
import { useClient } from '../../../hooks/useClient';
import { BreadcrumbI, Breadcrumbs } from '../../Breadcrumbs';
import {
  ItemFilter,
  ItemScopeEnum,
  VipItemsQuery,
  VipItemsQueryVariables
} from '../../../graphql/graphql';
import { FallbackView } from '../../FallbackView';

const queryLimit = 24;
const vippedListRef = React.createRef();
const EndlessVipItems = withEndlessScroll(SectionList);

interface VippedPageProps {
  adaptFilter: ItemFilter;
  breadcrumbs: BreadcrumbI[];
  isBaseVipPage: boolean;
  ssrError?: boolean;
}

export const VippedPage: React.FC<VippedPageProps> = ({
  adaptFilter,
  breadcrumbs,
  isBaseVipPage,
  ssrError
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { isSmall } = useScreenSize();
  const isClient = useClient();

  const [receivesItems, setReceivesItems] = useState(24);
  const router = useRouter();
  const { isBookmarked } = useBookmarks();

  const vipItemsVars: VipItemsQueryVariables = {
    filter: {
      scope: ItemScopeEnum.Vipped,
      ...adaptFilter
    },
    limit: queryLimit,
    offset: 0
  };

  const { loading, data, error, fetchMore } = useQuery<VipItemsQuery>(
    vipItemsQuery,
    {
      variables: vipItemsVars,
      ssr: false,
      nextFetchPolicy: 'cache-first'
    }
  );

  const items: VipItemsQuery['items'] = data?.items || [];

  const itemsList = items.map(item => (
    <ItemCard key={item.id} {...item} inBookmarks={isBookmarked(item.id)} />
  ));

  useEffect(() => {
    if (error || ssrError) dispatch(setError(true));
  }, [error, ssrError]);

  useEffect(() => {
    router.beforePopState(({ options }) => {
      options.scroll = false;

      return true;
    });

    return () => setReceivesItems(queryLimit);
  }, []);

  const showOnMobile = (isSmall && isClient) || !isClient;

  const onLoadMore = () => {
    const currentLength = items.length;
    const newVipItemsQueryVars: VipItemsQueryVariables = {
      limit: queryLimit,
      offset: currentLength,
      filter: { scope: ItemScopeEnum.Vipped, ...adaptFilter }
    };

    fetchMore({
      query: vipItemsQuery,
      variables: newVipItemsQueryVars,
      notifyOnNetworkStatusChange: true
    } as Partial<
      FetchMoreQueryOptions<typeof newVipItemsQueryVars, VipItemsQuery>
    >).then(fetchMoreResult => {
      setReceivesItems(fetchMoreResult.data.items.length);
    });
  };

  const adaptiveTitle = useMemo(() => {
    if (isBaseVipPage) return t('pages.vip.title');
    return isSmall && adaptFilter?.leased !== undefined
      ? t(`main_menu.${adaptFilter.leased ? 'lease' : 'sale'}`)
      : t('sections.vipped.title');
  }, [isBaseVipPage, isClient, isSmall, adaptFilter, t]);

  return (
    <>
      <PageWrapper>
        <VipHeaderContainer>
          {!isBaseVipPage && (
            <Breadcrumbs
              breadcrumbs={breadcrumbs || []}
              cypress="vipped-breadcrumbs"
            />
          )}
          <AdaptivePageHeader
            title={isClient ? adaptiveTitle : ''}
            type="vip"
            hasFilters={false}
            pageHeaderDirection="row"
          >
            {!isBaseVipPage && (
              <AllVippedLinkContainer>
                <RightLink
                  url="/items/vipped"
                  urlTitle={t('pages.vip.title')}
                  cypress="vipped-section-title"
                />
              </AllVippedLinkContainer>
            )}
          </AdaptivePageHeader>
        </VipHeaderContainer>
        {!isBaseVipPage && showOnMobile && (
          <NewSectionHeader
            hideOnDesk
            url="/items/vipped"
            title={t('sections.vipped.title')}
            urlTitle={t('common.all')}
            headlineType="h3"
            cypress="vipped-section-title"
          />
        )}
        {!(loading && !itemsList.length) ? (
          <EndlessVipItems
            ref={vippedListRef}
            items={itemsList}
            loading={loading}
            listAdditionalClasses={`vip-page-list ${isBaseVipPage && 'vip-page-list--main'}`}
            isNewDesign
            isStopLoad={receivesItems < queryLimit}
            onLoadMore={onLoadMore}
          >
            {itemsList}
          </EndlessVipItems>
        ) : (
          <SectionPlaceholder noHeader rowCount={3}>
            {Array.from({ length: 5 }, (_, index) => (
              <ItemCardPlaceholder key={index} isCardsLayout />
            ))}
          </SectionPlaceholder>
        )}
        {!itemsList.length && !loading && (
          <FallbackView
            width={317}
            height={230}
            text={t('pages.vip.empty_list')}
            icon="/static/images/new_ad/icons/icon-not-found.png"
          />
        )}
      </PageWrapper>
    </>
  );
};
